// QuizPage.tsx
import { useMemo, useState, useCallback } from "react";
import { BookOpen, Sparkles } from "lucide-react";
import Quiz from "@/QUIZ/Quiz";
import islamData from "./quizdata/islam.json";
import { useMyDialog } from "@/context/MyDialogProvider";
import StartQuizDialog from "./StartQuizDialog";
import { QuizTopic, IslamQuizData, QuizResult } from "./types";
import { TopicCard } from "./TopicCard";
import { QuizNavbar } from "./QuizNavbar";

export default function QuizPage() {
  const { open, closeAll } = useMyDialog();
  const data = islamData as IslamQuizData;
  
  const [quizConfig, setQuizConfig] = useState({
    studentName: "",
    started: false,
    selectedTopicId: data.topics[0]?.id || "",
  });

  const selectedTopic = useMemo(() => 
    data.topics.find(topic => topic.id === quizConfig.selectedTopicId) || data.topics[0],
    [quizConfig.selectedTopicId, data.topics]
  );

  const handleStartQuiz = useCallback((topic: QuizTopic) => {
    setQuizConfig(prev => ({ ...prev, selectedTopicId: topic.id }));
    
    open({
      header: "",
      content: (
        <StartQuizDialog
          selectedTopic={topic}
          studentName={quizConfig.studentName}
          setStudentName={(name) => setQuizConfig(prev => ({ ...prev, studentName: name }))}
          onClose={closeAll}
          onStart={(name) => {
            setQuizConfig(prev => ({ ...prev, studentName: name, started: true }));
            closeAll();
          }}
        />
      ),
    });
  }, [open, closeAll, quizConfig.studentName]);

  const handleQuizComplete = useCallback((result: QuizResult) => {
    console.log("Quiz completed:", result);
    // Add any analytics or additional logic here
  }, []);

  const handleChangeTopic = useCallback(() => {
    setQuizConfig(prev => ({ ...prev, started: false }));
  }, []);

  if (quizConfig.started) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <QuizNavbar
          selectedTopic={selectedTopic}
          studentName={quizConfig.studentName}
          onChangeTopic={handleChangeTopic}
        />
        
        <div className="py-6">
          <Quiz
            title={selectedTopic.title}
            description={selectedTopic.description}
            studentName={quizConfig.studentName}
            questions={selectedTopic.questions}
            passingPercentage={selectedTopic.passingPercentage}
            primaryColor={selectedTopic.color}
            showExplanation={true}
            onComplete={handleQuizComplete}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <HeroSection />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7">
          {data.topics.map((topic) => (
            <TopicCard
              key={topic.id}
              topic={topic}
              isSelected={quizConfig.selectedTopicId === topic.id}
              onSelect={() => setQuizConfig(prev => ({ ...prev, selectedTopicId: topic.id }))}
              onStart={() => handleStartQuiz(topic)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const HeroSection = () => (
  <div className="max-w-7xl mx-auto px-4 md:px-6 pt-10 pb-6">
    <div className="rounded-[2rem] bg-black text-white p-8 md:p-12 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,white,transparent_40%)]" />
      <div className="relative z-10 max-w-3xl">
        <div className="w-16 h-16 rounded-3xl bg-white/10 backdrop-blur flex items-center justify-center mb-6">
          <Sparkles size={30} />
        </div>
        <h1 className="text-4xl md:text-6xl font-black leading-tight mb-5">
          Islamic Learning Quiz
        </h1>
        <p className="text-lg text-white/80 leading-relaxed">
          Interactive quizzes with certificates, explanations, progress tracking,
          and multiple Islamic learning topics.
        </p>
      </div>
    </div>
  </div>
);